import { useMemo } from 'react';
import { formatLocationDisplay, formatLocationOverride } from '../utils/locationUtils';
import { timeSince } from '../utils/dateUtils';

/**
 * Compute display data for a list (owned or collaboration)
 * @param {Object} list - The list object
 * @param {Set} visitedPlaceIds - Set of visited place IDs
 * @param {boolean} isCollaboration - Whether this is a collaboration list
 * @returns {Object} - List with _computed property containing display data
 */
function computeListDisplayData(list, visitedPlaceIds, isCollaboration = false) {
  const items = list.items || [];
  const itemCount = list.itemCount ?? items.length;

  const completedCount = items.filter(p =>
    p.placeId && visitedPlaceIds.has(p.placeId)
  ).length;

  const heroItem = list.heroItem || items.find(item => item.photoUrl);
  const lastUpdated = list.updatedAt || list.createdAt;

  const computed = {
    percentComplete: itemCount > 0 ? Math.round((completedCount / itemCount) * 100) : 0,
    placeCount: itemCount,
    heroItem,
    heroPlaceId: heroItem?.placeId,
    locationDisplay: formatLocationOverride(list.locationOverride) || formatLocationDisplay(list.location) || list.location,
    categoryDisplay: list.categoryOverride || list.category,
    followersCount: list.followersCount || list.followers || 0,
    isPrivate: list.visibility === 'private' || list.privacyStatus === 'private' || (!list.privacyStatus && !list.visibility && !list.isPublic),
    timeSince: timeSince(lastUpdated),
  };

  if (isCollaboration) {
    computed.ownerDisplay = list.ownerName || 'Unknown';
    computed.collaboratorCount = (list.collaborators || []).length;
  }

  return {
    ...list,
    _computed: computed,
  };
}

/**
 * Sort lists by pinned status and position/date
 * @param {Array} lists - Array of list objects
 * @returns {Array} - Sorted array with pinned lists first
 */
function sortLists(lists) {
  const pinnedLists = lists.filter(list => list.pinned);
  const unpinnedLists = lists.filter(list => !list.pinned);

  // Sort pinned by position
  pinnedLists.sort((a, b) => (a.position || 0) - (b.position || 0));

  // Sort unpinned by position if set, otherwise by updated date
  unpinnedLists.sort((a, b) => {
    const aHasPosition = a.position !== undefined && a.position !== null;
    const bHasPosition = b.position !== undefined && b.position !== null;

    if (aHasPosition && bHasPosition) {
      return a.position - b.position;
    }
    if (aHasPosition && !bHasPosition) return -1;
    if (!aHasPosition && bHasPosition) return 1;

    const dateA = a.updatedAt?._seconds || a.updatedAt?.seconds || new Date(a.updatedAt).getTime() / 1000 || 0;
    const dateB = b.updatedAt?._seconds || b.updatedAt?.seconds || new Date(b.updatedAt).getTime() / 1000 || 0;
    return dateB - dateA;
  });

  return [...pinnedLists, ...unpinnedLists];
}

/**
 * Hook to compute sorted lists with display data
 * @param {Array} lists - User's owned lists
 * @param {Array} collaborationLists - Lists user collaborates on
 * @param {Set} visitedPlaceIds - Set of visited place IDs
 * @returns {Object} - { sortedLists, pinnedLists, unpinnedLists, sortedCollaborationLists }
 */
export function useListsWithComputed(lists, collaborationLists, visitedPlaceIds) {
  const sortedLists = useMemo(() => {
    const sorted = sortLists(lists);
    return sorted.map(list => computeListDisplayData(list, visitedPlaceIds, false));
  }, [lists, visitedPlaceIds]);

  const pinnedLists = useMemo(() =>
    sortedLists.filter(list => list.pinned),
    [sortedLists]
  );

  const unpinnedLists = useMemo(() =>
    sortedLists.filter(list => !list.pinned),
    [sortedLists]
  );

  const sortedCollaborationLists = useMemo(() => {
    return collaborationLists.map(list => computeListDisplayData(list, visitedPlaceIds, true));
  }, [collaborationLists, visitedPlaceIds]);

  return {
    sortedLists,
    pinnedLists,
    unpinnedLists,
    sortedCollaborationLists,
  };
}

/**
 * Hook to calculate summary stats across all lists
 * @param {Array} lists - User's owned lists
 * @param {Array} collaborationLists - Lists user collaborates on
 * @param {Function} getDesignation - Function to get designation for a placeId
 * @returns {Object} - Summary statistics
 */
export function useSummaryStats(lists, collaborationLists, getDesignation) {
  return useMemo(() => {
    const allLists = [...lists, ...collaborationLists];
    const uniquePlaceIds = new Set();

    allLists.forEach(list => {
      (list.items || []).forEach(place => {
        if (place.placeId) {
          uniquePlaceIds.add(place.placeId);
        }
      });
    });

    let gem = 0, highlyRecommended = 0, visited = 0, avoid = 0, wantToGo = 0;
    uniquePlaceIds.forEach((placeId) => {
      const badge = getDesignation(placeId)?.badge;
      if (badge === 'gem') gem++;
      else if (badge === 'highly-recommended') highlyRecommended++;
      else if (badge === 'visited') visited++;
      else if (badge === 'avoid') avoid++;
      else if (badge === 'want-to-go') wantToGo++;
      else wantToGo++;
    });

    const totalPlaces = uniquePlaceIds.size;
    const totalVisited = gem + highlyRecommended + visited + avoid;
    const percentVisited = totalPlaces > 0 ? Math.round((totalVisited / totalPlaces) * 100) : 0;

    return {
      totalPlaces,
      gem,
      highlyRecommended,
      visited,
      avoid,
      wantToGo,
      totalVisited,
      percentVisited,
    };
  }, [lists, collaborationLists, getDesignation]);
}

/**
 * Hook to filter lists by search query
 * @param {Array} lists - Array of lists to filter
 * @param {string} searchQuery - Search query string
 * @returns {Array} - Filtered lists
 */
export function useFilteredLists(lists, searchQuery) {
  return useMemo(() => {
    if (!searchQuery.trim()) return lists;

    const query = searchQuery.toLowerCase().trim();
    return lists.filter(list => {
      const title = (list.title || list.name || '').toLowerCase();
      if (title.includes(query)) return true;

      const location = list._computed?.locationDisplay?.toLowerCase() || '';
      if (location.includes(query)) return true;

      const category = list._computed?.categoryDisplay?.toLowerCase() || '';
      if (category.includes(query)) return true;

      const placeNames = list.placeNames || [];
      if (placeNames.some(name => name.toLowerCase().includes(query))) return true;

      const items = list.items || [];
      const matchesPlace = items.some(item => {
        const placeName = (item.name || '').toLowerCase();
        return placeName.includes(query);
      });
      if (matchesPlace) return true;

      return false;
    });
  }, [lists, searchQuery]);
}
